// Optimized Box-Muller transform with cached second value
let boxMullerSpare = null;
let hasSpare = false;

function boxMullerTransform() {
    if (hasSpare) {
        hasSpare = false;
        return boxMullerSpare;
    }
    
    hasSpare = true;
    let u = 0, v = 0;
    while(u === 0) u = Math.random();
    while(v === 0) v = Math.random();
    
    const mag = Math.sqrt(-2.0 * Math.log(u));
    const phase = 2.0 * Math.PI * v;
    
    boxMullerSpare = mag * Math.sin(phase);
    return mag * Math.cos(phase);
}

function getNormalRandom(mean, stddev) {
    return mean + boxMullerTransform() * stddev;
}

export function runAuctioneerSimulation({ bidderProfiles, qualityWeights, reservePrice, numSimulations, auctionType, iteration }) {
    // Validate inputs
    if (!bidderProfiles || bidderProfiles.length === 0) {
        return { expectedWinningBid: 0, winningBidStdDev: 0, averageWinnerTechScore: 0, averageWinnerExpScore: 0 };
    }
    
    let totalWinningBid = 0;
    let totalWinnerTechScore = 0;
    let totalWinnerExpScore = 0;
    let winningBids = [];
    let validAuctions = 0;

    const priceW = qualityWeights.priceWeight / 100;
    const techW = qualityWeights.techWeight / 100;
    const expW = qualityWeights.expWeight / 100;

    for (let i = 0; i < numSimulations; i++) {
        const allBidders = [];

        // Generate bidders based on profiles
        bidderProfiles.forEach((profile, profileIndex) => {
            for (let j = 0; j < profile.count; j++) {
                const trueCost = getNormalRandom(profile.costMean, profile.costStdDev);
                let bid;
                
                if (auctionType === 'FIRST_PRICE' || auctionType === 'DUTCH') {
                    // In procurement first-price auctions: bidders apply markup above cost
                    // Markup depends on competition level - more bidders = lower markup
                    const totalBidders = bidderProfiles.reduce((sum, p) => sum + p.count, 0);
                    const markup = totalBidders / (totalBidders - 1); // Revenue equivalence theory
                    bid = trueCost * markup;
                } else { 
                    // English/Vickrey: bidders apply Winner's Curse correction to avoid losses
                    const totalBidders = bidderProfiles.reduce((sum, p) => sum + p.count, 0);
                    const uncertainty = profile.costStdDev; // Use bidder's own uncertainty
                    bid = trueCost + ((totalBidders - 1) / (totalBidders + 1)) * uncertainty;
                }
                
                // Add some variation in quality scores
                const techScore = getNormalRandom(profile.techScore, 3);
                const expScore = getNormalRandom(profile.expScore, 4);
                
                allBidders.push({
                    id: `bidder-${profileIndex}-${j}`,
                    bid: Math.max(bid, trueCost * 1.02), // Minimum 2% markup to ensure profit
                    techScore: Math.max(0, Math.min(100, techScore)),
                    expScore: Math.max(0, Math.min(100, expScore)),
                    trueCost: trueCost
                });
            }
        });

        if (allBidders.length === 0) continue;

        // Calculate price scores (lower bid = higher price score in procurement)
        const minBid = Math.min(...allBidders.map(b => b.bid));
        const maxBid = Math.max(...allBidders.map(b => b.bid));

        allBidders.forEach(bidder => {
            let priceScore = 0;
            if (maxBid > minBid) {
                priceScore = ((maxBid - bidder.bid) / (maxBid - minBid)) * 100;
            } else {
                priceScore = 100; // All bids are equal
            }
            
            bidder.priceScore = Math.max(0, Math.min(100, priceScore));
            bidder.totalScore = (bidder.priceScore * priceW) + (bidder.techScore * techW) + (bidder.expScore * expW);
        });

        // Filter bidders within reserve price
        const eligibleBidders = allBidders.filter(b => b.bid <= reservePrice);
        
        if (eligibleBidders.length === 0) {
            // No valid bids - auction fails
            continue;
        }

        // Sort by total score (highest wins)
        eligibleBidders.sort((a, b) => b.totalScore - a.totalScore);
        const winner = eligibleBidders[0];

        let winningPayment;
        if (auctionType === 'FIRST_PRICE' || auctionType === 'DUTCH') {
            // Winner pays their bid
            winningPayment = winner.bid;
        } else {
            // English/Vickrey: winner pays second-price equivalent
            // In multi-attribute context, this is the bid that would tie for first place
            if (eligibleBidders.length > 1) {
                const secondPlace = eligibleBidders[1];
                // Calculate what bid would give winner the same score as second place
                // Solving: (priceScore(x) * priceW) + (techScore * techW) + (expScore * expW) = secondPlace.totalScore
                const nonPriceComponent = (winner.techScore * techW) + (winner.expScore * expW);
                const requiredPriceScore = (secondPlace.totalScore - nonPriceComponent) / priceW;
                
                // Convert required price score back to bid amount
                if (maxBid > minBid && requiredPriceScore <= 100) {
                    const calculatedBid = maxBid - (requiredPriceScore / 100) * (maxBid - minBid);
                    winningPayment = Math.min(Math.max(calculatedBid, minBid), winner.bid);
                } else {
                    winningPayment = winner.bid; // Fallback to first-price
                }
            } else {
                winningPayment = winner.bid; // Only one bidder
            }
        }

        validAuctions++;
        totalWinningBid += winningPayment;
        totalWinnerTechScore += winner.techScore;
        totalWinnerExpScore += winner.expScore;
        winningBids.push(winningPayment);
    }
    
    if (validAuctions === 0) {
        return { expectedWinningBid: reservePrice, winningBidStdDev: 0, averageWinnerTechScore: 0, averageWinnerExpScore: 0 };
    }

    const expectedWinningBid = totalWinningBid / validAuctions;
    const averageWinnerTechScore = totalWinnerTechScore / validAuctions;
    const averageWinnerExpScore = totalWinnerExpScore / validAuctions;
    
    // Calculate standard deviation of winning bids
    const variance = winningBids.reduce((sum, bid) => sum + Math.pow(bid - expectedWinningBid, 2), 0) / validAuctions;
    const winningBidStdDev = Math.sqrt(variance);

    return { 
        expectedWinningBid, 
        winningBidStdDev, 
        averageWinnerTechScore, 
        averageWinnerExpScore 
    };
} 